import React from 'react';

import { IonImg } from '@ionic/react';

import he from 'he';
import ReactMarkdown, { Components } from 'react-markdown';

// strip the <p> tag from the markdown in the text preview
const customRenderers: Partial<Components> = {
    p: ({ ...props }) => <>{props.children}</>,
  };
  
  function translateAndParseMarkdown(text: string): JSX.Element {
    if (!text) {
      return <span>N/A</span>;
    }
    const decodedText = he.decode(text);
    return <ReactMarkdown components={customRenderers}>{decodedText}</ReactMarkdown>;
  }
  
export interface UserData {
    uid: number;
    username: string;
    userslug: string;
    picture: string;
    status: string;
    lastonline: number;
    displayname: string;
    "icon:text": string;
    "icon:bgColor": string;
  }
  
export interface ChatMessage {
    fromuid: number;
    content: string;
    timestamp: number;
    user: UserData;
    fromUser: UserData;
    displayname: string;
    system: boolean;
    messageId: number;
    parent: ChatMessage | undefined;
    newSet: boolean;
  }
  
export interface ChatRoom {
    roomId: number;
    timestamp: number;
    roomName: string;
    groupChat: boolean;
    teaser: ChatMessage;
    messages: ChatMessage[];
    unread: boolean;
    lastUser: UserData;
    users: UserData[];
    userCount: number;
  }
  
export interface LoginData {
  uid: number;
  username: string;
  userslug: string;
  picture: string;
  status: string;
  postcount: number;
  reputation: number;
  lastonline: number;
  displayname: string;
  customFields: {
    name: string;
    type: string;
    options: string;
  }[];
  wrappedCookies: string;
  csrfToken: string;
}
  
  const getTextAvatar = (color: string, text: string) => {
    return `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="${color}"/><text x="50%" y="50%" font-family="Arial" font-size="50" dy=".375em" fill="white" text-anchor="middle">${text}</text></svg>`)}`;
  }

  const calculateRoomName = (room: ChatRoom, myUid = -1) => {
    let { roomName } = room;
    const { userCount, groupChat } = room;
    if (!groupChat || roomName === "") {
      // we don't have names for individual chats
      if (userCount === 1) {
        // ya alone, son
        roomName = "(empty room)";
      }
      else if (userCount === 2) {
        // grab the first user from the list of users, that isn't "You"
        let user = room?.users?.[0];
        for (const u of room?.users ?? []) {
          if (u.uid !== myUid) {
            user = u;
            break;
          }
        }
        roomName = user.displayname;
      } else {
        // grab up to 3 names, and then abbreviate the rest
        const { users } = room;
        const names = users.map(user => user.displayname);
        roomName = names.slice(0, 3).join(", ");
        if (users.length > 3) {
          const otherUserCount = users.length - 3;
          roomName += ` & ${otherUserCount} other${otherUserCount > 1 ? 's' : ''}`;
        }
      }
    }
    return roomName;
  }
  
  const baseUrl = "https://browsedns.net";
  const baseApiUrl = "https://api.browsedns.net";


const fallbackAvatarCache: { [key: number]: string } = {};

const defaultAvatar = getTextAvatar("#3f3f3f", "?");

const calculateAvatar = (curUser: UserData) => {
  let avatarUrl = defaultAvatar;

  if (curUser && curUser.picture) {
    avatarUrl = baseApiUrl + curUser.picture;
  }

  if (fallbackAvatarCache[curUser?.uid]) {
    // we've previously failed to load, so re-use the fallback
    avatarUrl = fallbackAvatarCache[curUser.uid];
  }

  return <IonImg
    src={avatarUrl}
    alt={curUser?.displayname}
    onIonError={(e: Event) => {
      if (curUser) {
        const bgColor = curUser['icon:bgColor'];
        const text = curUser['icon:text'];

        if (bgColor && text) {
          const fallbackAvatar = getTextAvatar(bgColor, text);
          if (e.target) {
            const target = e.target as HTMLImageElement;
             target.src = fallbackAvatar;
          }
          // update our cache of fallback avatars for next time
          fallbackAvatarCache[curUser.uid] = fallbackAvatar;
        }
      }
    }}
    className="menu-avatar"
  />
}

const calculateMultiAvatars = (chatPage: ChatRoom) => {
  const { lastUser, teaser } = chatPage;
  // returns 2 or 3 avatar circles that will be arranged in a container
  if (chatPage.users.length <= 1) {
    return calculateAvatar(lastUser);
  }

  let teaserUser: UserData | null = null;
  if (teaser && teaser.user) {
    teaserUser = teaser.user;
  }

  // copy the array of users in this room, and remove both the last user and teaser users
  const usersInRoom = chatPage.users.filter(user => user.uid !== lastUser?.uid && user.uid !== teaserUser?.uid);

  // shuffle the array so we don't always get the same users present
  // TODO: actually order by who last spoke
  // usersInRoom = usersInRoom.sort(() => Math.random() - 0.5);

  // make sure that the teaser user, and the last user to speak are at the front of the list
  if (lastUser) {
    usersInRoom.unshift(lastUser);
  }
  if (teaserUser && teaserUser.uid !== lastUser?.uid) {
    usersInRoom.unshift(teaserUser);
  }

  // grab top up to 3 users
  const avatars = usersInRoom.slice(0, 3).map(user => calculateAvatar(user));
  avatars.reverse();

  return (
    <div className="menu-avatar-container">
      {avatars}
    </div>
  );
}

  export { translateAndParseMarkdown, getTextAvatar, baseUrl, calculateRoomName, calculateMultiAvatars, calculateAvatar, defaultAvatar };